import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Boxes, ExternalLink, Star, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import Header from '../Header';
import Footer from '../Footer';
import LoginModal from '../LoginModal';
import SEO from '../SEO';

const FEATURED = {
  name: 'Plataforma BIM BR',
  org: 'ABDI — Agência Brasileira de Desenvolvimento Industrial',
  url: 'https://plataformabimbr.abdi.com.br/',
  description:
    'A maior biblioteca nacional de componentes BIM. Iniciativa pública que reúne objetos e famílias de fabricantes brasileiros em um só lugar, gratuitamente.',
};

const LIBRARIES = [
  { name: 'BIMobject', tag: 'Plataforma global', url: 'https://www.bimobject.com/pt-br', description: 'Plataforma com milhares de objetos BIM de fabricantes do mundo todo.' },
  { name: 'Família BIM', tag: 'Plataforma', url: 'https://www.familiabim.com.br/', description: 'Famílias e componentes BIM para Revit, em português.' },
  { name: 'Blocks RVT', tag: 'Plataforma', url: 'https://www.blocksrvt.com/pt/home', description: 'Blocos, famílias e conteúdo para Revit.' },
  { name: 'Saint-Gobain', tag: 'Fabricante', url: 'https://bimlibrary.saint-gobain.com/pt/Brazil/', description: 'Biblioteca BIM de sistemas construtivos Saint-Gobain.' },
  { name: 'Gerdau', tag: 'Fabricante', url: 'https://voce.mais.gerdau.com.br/bibliotecas-bim', description: 'Objetos BIM de produtos siderúrgicos e estruturas em aço.' },
  { name: 'Duratex / Deca', tag: 'Fabricante', url: 'https://materiais.duratex.com.br/cadastro-catalogo-bim', description: 'Catálogo BIM de louças, metais e painéis.' },
  { name: 'Amanco', tag: 'Fabricante', url: 'https://bim.amanco.com.br/librerias-bim/', description: 'Bibliotecas BIM de sistemas hidráulicos e prediais.' },
  { name: 'ArcelorMittal', tag: 'Fabricante', url: 'https://brasil.arcelormittal.com/biblioteca-bim', description: 'Biblioteca BIM de perfis e soluções em aço.' },
  { name: 'Knauf', tag: 'Fabricante', url: 'https://knauf.com/pt-BR/nossas-ferramentas/bim', description: 'Objetos BIM de sistemas em drywall e placas.' },
  { name: 'Tramontina', tag: 'Fabricante', url: 'https://global.tramontina.com/arquivos-bim', description: 'Arquivos BIM de produtos, ferragens e acabamentos.' },
  { name: 'Tigre', tag: 'Fabricante', url: 'https://www.tigre.com.br/tigre-bim#!/explorer', description: 'Objetos BIM de tubos, conexões e sistemas prediais.' },
  { name: 'DNIT', tag: 'Órgão público', url: 'https://www.gov.br/dnit/pt-br/assuntos/planejamento-e-pesquisa/bim-no-dnit/menu-de-categorias/bim-no-dnit/contratacoes-bim/biblioteca', description: 'Biblioteca BIM voltada à infraestrutura de transportes.' },
];

const TAG_STYLES = {
  'Plataforma global': 'bg-violet-500/15 text-violet-300 border-violet-500/30',
  Plataforma: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
  Fabricante: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  'Órgão público': 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
};

const LibraryPage = () => {
  const navigate = useNavigate();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const open = (url) => window.open(url, '_blank', 'noopener,noreferrer');

  return (
    <>
      <SEO
        title="AJ | BIM Library - Bibliotecas BIM Gratuitas"
        description="Reunimos as principais bibliotecas BIM gratuitas de fabricantes da construção civil e a maior biblioteca nacional, a Plataforma BIM BR."
        keywords="biblioteca BIM, famílias Revit, objetos BIM, BIM BR, componentes BIM gratuitos"
      />
      <Header onLoginClick={() => setIsLoginModalOpen(true)} />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden" data-testid="library-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-900/60 via-black to-black" />
        <div className="relative container mx-auto px-4 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-6 text-sm"
            data-testid="library-back-btn"
          >
            <ArrowLeft size={16} /> Voltar
          </button>
          <div className="max-w-3xl">
            <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/30 mb-4 inline-flex items-center gap-1">
              <Boxes size={14} /> AJ | BIM Library
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Bibliotecas BIM <span className="text-violet-400">gratuitas</span> em um só lugar
            </h1>
            <p className="text-lg md:text-xl text-gray-200 max-w-2xl">
              Reunimos as principais bibliotecas de objetos e famílias BIM de fabricantes da construção civil,
              além da maior biblioteca nacional — a Plataforma BIM BR.
            </p>
          </div>
        </div>
      </section>

      {/* Featured: BIM BR */}
      <section className="pb-6" data-testid="library-featured">
        <div className="container mx-auto px-4 lg:px-8">
          <Card className="bg-gradient-to-br from-violet-900/40 to-zinc-900 border-violet-500/40 overflow-hidden">
            <CardContent className="p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-1">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-full px-3 py-1 mb-3">
                  <Star size={13} /> Destaque nacional
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-1">{FEATURED.name}</h2>
                <p className="text-violet-300 text-sm mb-3">{FEATURED.org}</p>
                <p className="text-gray-300 text-sm md:text-base leading-relaxed max-w-2xl">{FEATURED.description}</p>
              </div>
              <Button
                onClick={() => open(FEATURED.url)}
                className="bg-violet-600 hover:bg-violet-500 text-white shrink-0"
                data-testid="library-featured-btn"
              >
                <ExternalLink className="mr-2" size={18} /> Acessar BIM BR
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Grid de bibliotecas */}
      <section className="py-12 bg-black" data-testid="library-grid">
        <div className="container mx-auto px-4 lg:px-8">
          <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
            Bibliotecas de <span className="text-violet-400">fabricantes e plataformas</span>
          </h3>
          <p className="text-gray-400 text-sm mb-8">Todas gratuitas — alguns fabricantes podem solicitar cadastro para download.</p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {LIBRARIES.map((lib) => (
              <Card
                key={lib.name}
                className="bg-zinc-900 border-zinc-800 hover:border-violet-500/40 hover:-translate-y-1 transition-all duration-300 flex flex-col"
                data-testid={`library-card-${lib.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <CardContent className="p-5 flex flex-col h-full">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h4 className="text-lg font-bold text-white">{lib.name}</h4>
                    <span className={`text-[10px] font-semibold uppercase tracking-wide border rounded-full px-2.5 py-1 whitespace-nowrap ${TAG_STYLES[lib.tag] || 'bg-zinc-700 text-gray-200'}`}>
                      {lib.tag}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm leading-relaxed mb-5 flex-1">{lib.description}</p>
                  <Button
                    onClick={() => open(lib.url)}
                    variant="outline"
                    size="sm"
                    className="w-full border-violet-500/40 text-violet-300 hover:bg-violet-600 hover:text-white"
                    data-testid={`library-btn-${lib.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <Download className="mr-2" size={15} /> Acessar biblioteca
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-gray-500 text-xs text-center mt-10 max-w-2xl mx-auto">
            Os links direcionam para os sites oficiais de cada biblioteca. A AJ | BIM não se responsabiliza pelo
            conteúdo, disponibilidade ou termos de uso de plataformas de terceiros.
          </p>
        </div>
      </section>

      <Footer />
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
    </>
  );
};

export default LibraryPage;
